/**
 * BCC France — Lightbox (GLightbox)
 *
 * Activation rules per template context (detected via <body> classes):
 *
 *   single-post / single-information (auto mode)
 *     → Every <img> inside .entry-content gets a lightbox, even without a link.
 *     → Images inside .wp-block-gallery are grouped: prev/next navigation is available.
 *     → Images already linked to a media file reuse the existing <a>.
 *     → Images linked to a non-media URL are left untouched (respect editorial links).
 *
 *   page (link mode)
 *     → Only <img> already wrapped in an <a href="…media…"> get a lightbox.
 *     → No auto-wrapping.
 */
import GLightbox from 'glightbox';

/** Shared GLightbox config used for all contexts. */
const LIGHTBOX_CONFIG = {
  selector: '.lightbox',
  touchNavigation: true,    // Swipe prev/next on mobile
  keyboardNavigation: true, // Arrow keys + Escape
  // closeOnOutsideClick is intentionally omitted: GLightbox's built-in check
  // never fires because .ginner-container fills 100vw × 100vh, so every click
  // is detected as "inside" the container. We wire our own handler below.
  closeOnOutsideClick: false,
  loop: false,
  descPosition: 'bottom',
};

/**
 * Create a GLightbox instance and attach a manual close-on-outside-click
 * handler, since the native one is broken when the slide container fills
 * the full viewport.
 */
function createLightbox() {
  const lb = GLightbox( LIGHTBOX_CONFIG );

  lb.on( 'open', () => {
    const glContainer = document.querySelector( '.glightbox-container' );
    if ( ! glContainer ) return;

    // Click anywhere on a .gslide that is not the media or the caption closes.
    glContainer.querySelectorAll( '.gslide' ).forEach( ( slide ) => {
      slide.addEventListener( 'click', ( e ) => {
        if (
          ! e.target.closest( '.gslide-media' ) &&
          ! e.target.closest( '.gslide-description' ) &&
          ! e.target.closest( '.gbtn' )
        ) {
          lb.close();
        }
      } );
    } );
  } );

  return lb;
}

export function initLightbox() {
  const body = document.body;
  const isPost = body.classList.contains( 'single-post' );
  const isInfo = body.classList.contains( 'single-information' );
  const isPage = body.classList.contains( 'page' ) && ! body.classList.contains( 'single' );

  if ( ! isPost && ! isInfo && ! isPage ) return;

  const content = document.querySelector( '.entry-content' );
  if ( ! content ) return;

  if ( isPost || isInfo ) {
    initAutoLightbox( content );
  } else {
    initLinkedLightbox( content );
  }
}

// ============================================================
// AUTO MODE — Posts & Information CPT
// ============================================================

/**
 * Wrap all images in .entry-content for lightbox.
 * Gallery blocks (.wp-block-gallery) are grouped for prev/next navigation.
 */
function initAutoLightbox( content ) {
  let galleryIndex = 0;

  // Step 1: process gallery blocks — images are grouped together.
  content.querySelectorAll( '.wp-block-gallery' ).forEach( ( gallery ) => {
    galleryIndex++;
    const group = `bcc-gallery-${ galleryIndex }`;
    gallery.querySelectorAll( 'img' ).forEach( ( img ) => processImage( img, group ) );
  } );

  // Step 2: process standalone images not inside a gallery block.
  content.querySelectorAll( 'img' ).forEach( ( img ) => {
    if ( img.dataset.bccLightbox ) return;          // Already processed
    if ( img.closest( '.wp-block-gallery' ) ) return; // Handled above
    processImage( img, null );
  } );

  createLightbox();
}

/**
 * Handle a single image element in auto mode.
 *
 * Priority:
 *   1. img inside <a href="media"> → reuse anchor, add lightbox class.
 *   2. img inside <a href="other"> → skip (respect the editorial link).
 *   3. img without link            → wrap in a new <a> pointing to best-quality src.
 */
function processImage( img, gallery ) {
  img.dataset.bccLightbox = '1';

  const parent = img.parentElement;
  const isAnchor = parent && parent.tagName === 'A';

  if ( isAnchor && isMediaUrl( parent.href ) ) {
    // Already linked to the media file — just enable lightbox.
    parent.classList.add( 'lightbox' );
    if ( gallery ) parent.dataset.gallery = gallery;
    return;
  }

  if ( isAnchor ) {
    // Linked to a page/post — respect the navigation, skip lightbox.
    return;
  }

  // No anchor — wrap the image.
  const src = getBestSrc( img );
  const alt = img.getAttribute( 'alt' ) || '';
  const anchor = document.createElement( 'a' );
  anchor.href = src;
  anchor.classList.add( 'lightbox' );
  if ( alt ) anchor.dataset.title = alt;
  if ( gallery ) anchor.dataset.gallery = gallery;

  img.parentNode.insertBefore( anchor, img );
  anchor.appendChild( img );
}

// ============================================================
// LINK MODE — Pages
// ============================================================

/**
 * Only enable lightbox for images already wrapped in a media link.
 * No DOM modifications other than adding the class.
 */
function initLinkedLightbox( content ) {
  let hasAny = false;

  content.querySelectorAll( 'a[href]' ).forEach( ( link ) => {
    if ( ! isMediaUrl( link.href ) ) return;
    if ( ! link.querySelector( 'img' ) ) return;
    link.classList.add( 'lightbox' );
    hasAny = true;
  } );

  if ( hasAny ) {
    createLightbox();
  }
}

// ============================================================
// HELPERS
// ============================================================

/**
 * Return the highest-resolution URL from srcset, falling back to src.
 */
function getBestSrc( img ) {
  if ( ! img.srcset ) return img.src;

  const entries = img.srcset
    .split( ',' )
    .map( ( s ) => {
      const parts = s.trim().split( /\s+/ );
      const url = parts[ 0 ];
      const w = parts[ 1 ] ? parseInt( parts[ 1 ] ) : 0;
      return { url, w };
    } )
    .sort( ( a, b ) => b.w - a.w );

  return entries.length ? entries[ 0 ].url : img.src;
}

/**
 * Return true when the URL points directly to an image media file.
 */
function isMediaUrl( href ) {
  if ( ! href ) return false;
  return /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i.test( href );
}
